import supabase from './supabaseClient';
import EdgeFunctionClient from './edgeFunctionClient';
import config from '../config';

export class LoginResponseError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

const UserService = {
  async loginWithClient(client, { email, password }, dispatch) {
    try {
      const { data, error } = await client.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw new LoginResponseError(error.message, error.status || 400);
      }
      
      const authObject = { 
        user: data.user, 
        username: email, 
        isLoggedIn: true,
        session: data.session
      };
      dispatch({ type: 'LOGIN', payload: authObject });

      return authObject;
    } catch (error) {
      throw new LoginResponseError(error.message, error.status || 500);
    }
  },

  async logoutWithClient(client, dispatch, user = null) {
    try {
      console.log('UserService: Starting logout with client...');
      
      // Always clear local state first, regardless of Supabase session state
      if (user && user.value !== undefined) {
        user.value = null;
      }
      
      dispatch({ type: 'LOGOUT' });
      
      // Then attempt to sign out from Supabase
      const { error } = await client.auth.signOut();
      
      if (error) {
        console.warn('UserService: SignOut had an error, but local state is cleared:', error);
        // Don't throw the error since local cleanup was successful
      } else {
        console.log('UserService: Supabase signOut successful');
      }
      
      console.log('UserService: Logout complete');
    } catch (error) {
      console.error('UserService: Logout error:', error);
      
      // Even if there's an error, ensure local state is cleared
      if (user && user.value !== undefined) {
        user.value = null;
      }
      dispatch({ type: 'LOGOUT' });
      
      // Don't throw the error - logout should always succeed locally
      console.log('UserService: Logout completed despite errors - local state cleared');
    }
  },

  // Legacy methods for backward compatibility
  async login({ username, password }, dispatch) {
    try {
      // Use Supabase client directly to ensure proper session handling
      const { data, error } = await supabase.auth.signInWithPassword({
        email: username,
        password: password
      });

      if (error) {
        throw new LoginResponseError(error.message, error.status || 400);
      }
      
      const authObject = { 
        user: data.user, 
        username, 
        isLoggedIn: true,
        session: data.session
      };
      dispatch({ type: 'LOGIN', payload: authObject });

      return authObject;
    } catch (error) {
      throw new LoginResponseError(error.message, error.status || 500);
    }
  },

  async logout(dispatch) {
    try {
      console.log('UserService: Starting legacy logout...');
      
      // Clear local state first
      dispatch({ type: 'LOGOUT' });
      
      // Then attempt to sign out from Supabase
      console.log('UserService: Calling supabase.auth.signOut...');
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.warn('UserService: SignOut had an error, but local state is cleared:', error);
        // Don't throw the error since local cleanup was successful
      } else {
        console.log('UserService: Supabase signOut successful');
      }
      
      console.log('UserService: Legacy logout complete');
    } catch (error) {
      console.error('UserService: Legacy logout error:', error);
      
      // Ensure local state is cleared even on error
      dispatch({ type: 'LOGOUT' });
      
      // Don't throw the error - logout should always succeed locally
      console.log('UserService: Legacy logout completed despite errors - local state cleared');
    }
  },

  async forgot({ username }) {
    if (!username) {
      throw new Error('Email address is required');
    }

    // Use the configuration for redirect URL
    const redirectTo = config.resetPasswordUrl;

    try {
      await EdgeFunctionClient.call('auth-reset-password', { 
        email: username, 
        redirectTo 
      });
      return true;
    } catch (error) {
      throw new LoginResponseError(error.message, error.status || 500);
    }
  },

  async reLogin(dispatch) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        dispatch({ type: 'LOGOUT' });
        return { isLoggedIn: false };
      }

      const newAuthObject = { 
        user: session.user, 
        username: session.user.email,
        isLoggedIn: true,
        session: session
      };
      dispatch({ type: 'LOGIN', payload: newAuthObject });

      return newAuthObject;
    } catch (error) {
      dispatch({ type: 'LOGOUT' });
      return { isLoggedIn: false };
    }
  },

  async saveNewPassword(newPasswordObject) {
    try {
      await EdgeFunctionClient.call('auth-update-password', { 
        newPassword: newPasswordObject.newPassword 
      });
      return true;
    } catch (error) {
      throw new LoginResponseError(error.message, error.status || 500);
    }
  },

  async resendConfirmationEmail({ username }) {
    try {
      await EdgeFunctionClient.call('auth-reset-password', { email: username });
      return true;
    } catch (error) {
      throw new LoginResponseError(error.message, error.status || 500);
    }
  },

  async signupWithClient(client, { email, password }) {
    try {
      const { data, error } = await client.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        throw new LoginResponseError(error.message, error.status || 400);
      }

      return { user: data.user, session: data.session };
    } catch (error) {
      throw new LoginResponseError(error.message, error.status || 500);
    }
  },

  async signup({ email, password }) {
    try {
      // Use Supabase client directly to ensure proper session handling
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        throw new LoginResponseError(error.message, error.status || 400);
      }

      return { user: data.user, session: data.session };
    } catch (error) {
      throw new LoginResponseError(error.message, error.status || 500);
    }
  },

  handleOAuthCallback: async (client, dispatch) => {
    try {
      const { data, error } = await client.auth.getSession();
      
      if (error) {
        throw error;
      }

      if (data.session) {
        dispatch({
          type: 'LOGIN',
          payload: {
            user: data.session.user,
            session: data.session,
            isLoggedIn: true
          }
        });

        return {
          isLoggedIn: true,
          user: data.session.user,
          session: data.session
        };
      }

      return { isLoggedIn: false };
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  },
};

export default UserService;
